import React from 'react';
import styled from 'styled-components';
import { FiEdit, FiCpu, FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const HeaderInfo = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const ClienteName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const AmbienteBadge = styled.span`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${props => props.$ambiente === 'PRD' ? theme.colors.success[500] : theme.colors.warning[500]};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const DetailLabel = styled.span`
  color: ${theme.colors.text.muted};
  min-width: 100px;
`;

const DetailValue = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const getToneLabel = (tone) => {
  const tones = {
    formal: 'Formal',
    amigavel: 'Amigável',
    casual: 'Casual',
    tecnico: 'Técnico',
    vendedor: 'Vendedor',
    consultivo: 'Consultivo'
  };
  return tones[tone] || tone;
};

const getEmojisLabel = (emojis) => {
  const labels = {
    NONE: 'Nenhum',
    LOW: 'Baixo',
    MEDIUM: 'Médio',
    HIGH: 'Alto'
  };
  return labels[emojis] || emojis;
};

const ConfiguracaoIACard = ({ parametro }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/configuracao-ia/${parametro.uuid || parametro.id}/editar`);
  };

  // Exibe o agent_name se existir, senão mostra "Assistente IA"
  const nomeIA = parametro.agent_name || 'Assistente IA';

  return (
    <CardContainer>
      <Header>
        <IconWrapper>
          <FiCpu />
        </IconWrapper>
        <HeaderInfo>
          <ClienteName>{parametro.cliente_nome || 'Cliente não informado'}</ClienteName>
          <AmbienteBadge $ambiente={parametro.ambiente}>
            {parametro.ambiente}
          </AmbienteBadge>
        </HeaderInfo>
      </Header>

      <ContentSection>
        <Details>
          <DetailItem>
            <DetailIcon><FiCpu /></DetailIcon>
            <DetailLabel>Nome da IA:</DetailLabel>
            <DetailValue>{nomeIA}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiSettings /></DetailIcon>
            <DetailLabel>Tom:</DetailLabel>
            <DetailValue>{getToneLabel(parametro.ia_tone)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiSettings /></DetailIcon>
            <DetailLabel>Áudio:</DetailLabel>
            <DetailValue>{parametro.responder_por_audio ? 'Sim' : 'Não'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiSettings /></DetailIcon>
            <DetailLabel>Emojis:</DetailLabel>
            <DetailValue>{getEmojisLabel(parametro.emojis)}</DetailValue>
          </DetailItem>
        </Details>

        <Actions style={{ marginTop: theme.spacing[4] }}>
          <ActionButton $primary onClick={handleEdit}>
            <ActionIcon><FiEdit /></ActionIcon>
            Personalizar
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default ConfiguracaoIACard;
